import { useState } from "react";
import { motion } from "framer-motion";
import { regions, offices } from "@/content/site";
import { DotField, MAP_H, MAP_W, project } from "./DotField";

// Approximate visual centre of each region, used for the connecting arcs.
const CENTRES = {
  europe: [50, 12], latam: [-14, -58], mena: [26, 42], cis: [57, 70],
  india: [21, 78], sea: [6, 108], korea: [36.5, 127.8], anz: [-26, 138],
};
const HUB = project(...CENTRES.india);

const arc = (to) => {
  const [x2, y2] = project(...to);
  const [x1, y1] = HUB;
  const mx = (x1 + x2) / 2;
  const my = Math.min(y1, y2) - Math.abs(x2 - x1) * 0.28 - 20;
  return `M${x1},${y1} Q${mx},${my} ${x2},${y2}`;
};

const RegionChip = ({ r, active, onSelect }) => (
  <button
    type="button"
    onMouseEnter={() => onSelect(r.id)}
    onFocus={() => onSelect(r.id)}
    onClick={() => onSelect(r.id)}
    aria-pressed={active}
    data-testid={`region-chip-${r.id}`}
    className={`group rounded-none border p-4 text-left transition-[background-color,border-color] duration-300 ${
      active ? "border-accent bg-accent-soft" : "border-line bg-surface hover:border-accent/50"
    }`}
  >
    <span className="flex items-center gap-2 text-[15px] font-medium text-ink">
      <span className={`h-2 w-2 rounded-full ${active ? "bg-accent" : "bg-accent/50"}`} />
      {r.name}
    </span>
    <span className="mt-1.5 block text-[13px] leading-snug text-muted">{r.note}</span>
  </button>
);

export const WorldMap = () => {
  const [active, setActive] = useState("india");
  const ids = regions.map((r) => r.id);
  const activeRegion = regions.find((r) => r.id === active);
  const [lx, ly] = project(...CENTRES[active]);

  return (
    <div data-testid="world-map">
      <div className="relative overflow-hidden border border-line bg-surface p-3 sm:p-6">
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="h-auto w-full" role="img" aria-label="World map highlighting the regions where SPC works">
          <DotField highlight={ids} active={active} />
          {ids
            .filter((id) => id !== "india")
            .map((id, i) => (
              <motion.path
                key={id}
                d={arc(CENTRES[id])}
                fill="none"
                className="stroke-accent"
                strokeWidth={active === id ? 2.4 : 1.2}
                strokeDasharray={active === id ? "0" : "4 6"}
                opacity={active === id ? 1 : 0.55}
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 1.6, delay: 0.2 + i * 0.12, ease: "easeInOut" }}
              />
            ))}
          {offices.map((o) => {
            const [x, y] = project(o.lat, o.lon);
            return (
              <g key={o.id} data-testid={`map-office-${o.id}`}>
                <circle cx={x} cy={y} r="16" className="fill-accent" opacity="0.18">
                  <animate attributeName="r" values="8;22;8" dur="3s" repeatCount="indefinite" />
                </circle>
                <circle cx={x} cy={y} r="7" className="fill-accent stroke-paper" strokeWidth="3" />
              </g>
            );
          })}
          <g transform={`translate(${Math.min(lx, MAP_W - 260)}, ${Math.max(ly - 46, 30)})`} className="pointer-events-none">
            <rect x="0" y="-28" rx="18" height="40" width={activeRegion.name.length * 13 + 40} className="fill-ink" />
            <text x="20" y="-2" className="fill-paper font-sans text-[20px] font-medium">
              {activeRegion.name}
            </text>
          </g>
        </svg>
        <div className="mt-3 flex flex-wrap items-center gap-5 px-2 font-mono text-[12px] uppercase tracking-[0.16em] text-muted">
          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-accent" /> Office</span>
          <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-accent/70" /> Active region</span>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {regions.map((r) => (
          <RegionChip key={r.id} r={r} active={active === r.id} onSelect={setActive} />
        ))}
      </div>
    </div>
  );
};
